import React, { useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Link, useMatch, useNavigate } from "react-router-dom";
import SidebarItem from "./SidebarItem";
import { ItemActions } from "./ItemActions";
import { Sparkles } from "lucide-react";
import { useItemManagement } from "@/hooks/useItemManagement";
import { ItemDropdownMenu } from "./ItemDropdownMenu";
import MiniSidebarSection from "./MiniSidebarSection";
import { SystemWorkspaceIcon } from "@/components/icons";
import type { CustomWorkspace } from "@/types/customWorkspace";
import { useAuthenticationContext } from "@/store/authContext/authContext";
import EventBus from "@/lib/eventBus";
import mixpanel from "@/lib/mixpanel";

type Workspace = {
  id: string;
  title: string;
  path: string;
};

interface SidebarWorkspaceSectionProps {
  workspaces: Workspace[];
  isExpanded: boolean;
  onToggle: (value: boolean) => void;
  onModalStateChange: (isOpen: boolean) => void;
  isActive: boolean;
  isMiniMode: boolean;
}

const SidebarWorkspaceSection: React.FC<SidebarWorkspaceSectionProps> = ({
  workspaces,
  isExpanded,
  onToggle,
  onModalStateChange,
  isActive,
  isMiniMode,
}) => {
  const matchCurrentWorkspace = useMatch("/workspace/:id");
  const matchNewWorkspace = useMatch("/new-workspace");
  const navigate = useNavigate();
  const { state, dispatch } = useAuthenticationContext();

  const customWorkspacesState =
    state.customWorkspaceState.sidebarCustomWorkspaces;
  const customWorkspaces = customWorkspacesState.data;

  const refreshCustomWorkspaces = useCallback(() => {
    console.log("Refreshing custom workspaces");
    EventBus.$emit("sidebar:refreshCustomWorkspaces");
  }, []);

  useEffect(() => {
    refreshCustomWorkspaces();
  }, [
    customWorkspacesState.params.query,
    customWorkspacesState.params.sort,
    refreshCustomWorkspaces,
  ]);

  const {
    selectedItem: selectedCustomWorkspace,
    setSelectedItem: setSelectedCustomWorkspace,
    isRenameOpen,
    setIsRenameOpen,
    isDeleteOpen,
    setIsDeleteOpen,
    handleRename,
    handleDelete,
  } = useItemManagement(
    "workspaces",
    refreshCustomWorkspaces,
    "/new-workspace",
    "workspace",
  );

  const location = useLocation();

  useEffect(() => {
    // Reset rename state and selected workspace when the route changes.
    // Do NOT reset delete here — closing delete on every render prevented the
    // delete modal from staying open. This mirrors the report section behavior.
    setIsRenameOpen(false);
    setSelectedCustomWorkspace(null);
  }, [location, setIsRenameOpen, setSelectedCustomWorkspace]);

  const handleNewWorkspaceClick = () => {
    if (matchNewWorkspace) {
      // If we are already on the new workspace page, reset the workspace state
      EventBus.$emit("workspace:resetWorkspace");
    }
    mixpanel.track("Click", {
      eventDescription: "Smart Workspaces > New Workspace",
    });
    // Navigate to the new workspace page
    navigate("/new-workspace");
  };

  // Update parent modal state when either popover or dialogs are open
  useEffect(() => {
    onModalStateChange(isRenameOpen || isDeleteOpen);
  }, [isRenameOpen, isDeleteOpen, onModalStateChange]);

  const renderWorkspaceItem = (
    workspace: CustomWorkspace,
    isActive?: boolean,
  ) => (
    <div key={workspace.id} className="flex items-center group">
      <Link
        to={`/workspace/${workspace.id}`}
        className={`flex-1 min-w-0 ${isActive ? "text-accent-foreground" : ""}`}
        onClick={() =>
          mixpanel.track("Click", {
            eventDescription: `Smart Workspaces > ${workspace.title}`,
          })
        }
      >
        <Button
          variant="ghost"
          className={`rounded-[20px] pl-3 w-full justify-start pt-1 mt-0 pb-1.5 h-auto text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${
            isActive ? "font-bold text-foreground dark:text-foreground" : ""
          }`}
          title={workspace.title}
        >
          <div className="truncate">{workspace.title}</div>
        </Button>
      </Link>
      <ItemDropdownMenu
        config={{
          isDuplicateEnabled: false,
          isDeleteEnabled: true,
          isRenameEnabled: true,
        }}
        onRename={() => {
          setSelectedCustomWorkspace(workspace);
          setIsRenameOpen(true);
        }}
        onDelete={() => {
          setSelectedCustomWorkspace(workspace);
          setIsDeleteOpen(true);
        }}
      />
    </div>
  );

  const renderWorkspaceContent = (
    <>
      <div className="pl-8">
        <a
          className="cursor-pointer font-bold block text-primary pl-3 pb-1.5 hover:underline"
          onClick={handleNewWorkspaceClick}
        >
          + New Workspace
        </a>

        {workspaces.map((workspace) => {
          const workspaceId = workspace.path.split("/").pop();
          const isActive = matchCurrentWorkspace?.params.id === workspaceId;

          return (
            <React.Fragment key={workspaceId}>
              <Link
                to={workspace.path}
                className={`block ${isActive ? "text-accent-foreground" : ""}`}
                onClick={() =>
                  mixpanel.track("Click", {
                    eventDescription: `Smart Workspaces > ${workspace.title}`,
                  })
                }
              >
                <Button
                  variant="ghost"
                  className={`w-full justify-start px-3 py-1.5 h-auto text-gray-500 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors ${
                    isActive
                      ? "font-bold text-foreground dark:text-foreground bg-sky-50 dark:bg-sky-900/20"
                      : ""
                  }`}
                  title={workspace.title}
                >
                  <div className="inline-block mr-0 w-[20px] color-primary">
                    <SystemWorkspaceIcon />
                  </div>

                  <div className="truncate">{workspace.title}</div>
                </Button>
              </Link>
            </React.Fragment>
          );
        })}
      </div>
      <hr className="!my-5" />
      <div className="pl-8">
        {customWorkspaces.length > 0 && (
          <>
            <div className="pb-2 pl-3 pt-2 text-gray-700 mb-2 dark:text-white">
              Sort by:{" "}
              <select
                value={
                  state.customWorkspaceState.sidebarCustomWorkspaces.params.sort
                }
                onChange={(e) => {
                  dispatch({
                    type: "setSideBarCustomWorkpacesParamsSort",
                    payload: e.target.value,
                  });
                }}
                className="bg-transparent font-bold text-sm border-none mx-1 p-0 pr-8 text-primary cursor-pointer focus:ring-0"
              >
                <option value="recent">Most Recent</option>
                <option value="alpha">Alphabetical</option>
              </select>
            </div>

            <div className="mb-4">
              {customWorkspaces.map((item) => {
                const activeItemId = matchCurrentWorkspace?.params.id;
                const isActive = activeItemId
                  ? parseInt(item.id) === parseInt(activeItemId)
                  : false;
                return renderWorkspaceItem(item, isActive);
              })}
            </div>
          </>
        )}
      </div>
    </>
  );

  const itemActions = (
    <ItemActions
      isRenameOpen={isRenameOpen}
      isDeleteOpen={isDeleteOpen}
      itemType="Workspace"
      onRenameClose={() => setIsRenameOpen(false)}
      onDeleteClose={() => setIsDeleteOpen(false)}
      onRename={handleRename}
      onDelete={handleDelete}
      initialTitle={selectedCustomWorkspace?.title}
      onModalStateChange={onModalStateChange}
    />
  );

  if (isMiniMode) {
    return (
      <>
        <MiniSidebarSection
          icon={<Sparkles size={20} />}
          label="Smart Workspaces"
          isActive={isActive}
        >
          {renderWorkspaceContent}
        </MiniSidebarSection>
        {itemActions}
      </>
    );
  }

  return (
    <>
      <div id="sidebar-smart-workspaces">
        <SidebarItem
          icon={
            <Sparkles
              size={20}
              color={
                isActive
                  ? "var(--sidebar-active-color)"
                  : "var(--sidebar-item-color)"
              }
            />
          }
          label="Smart Workspaces"
          isExpanded={isExpanded}
          onToggle={onToggle}
          isActive={isActive}
        >
          {renderWorkspaceContent}
        </SidebarItem>
      </div>
      {itemActions}
    </>
  );
};

export default SidebarWorkspaceSection;
